import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import {
  busyBody,
  busyQuery,
  defaultBusyFilters,
  emptyBusy,
  METRICS,
} from "../busy-times-utils";
import downloadCsv from "../download-csv";
import BusyFilters from "./BusyFilters";
import BusyHeatmap from "./BusyHeatmap";
import DailyVisitsChart from "./DailyVisitsChart";
import DoorEmpty from "./DoorEmpty";
import PeaksCard from "./PeaksCard";
import QuietBanner from "./QuietBanner";
import RetryLabel from "./RetryLabel";

const TOAST_MS = 6000;

const formatRange = (busy) => {
  const fmt = (day) => moment.utc(day, "YYYY-MM-DD").format("MMM D");
  return `${fmt(busy.effectiveFrom)} – ${fmt(busy.effectiveTo)}`;
};

const refresh = (body) => submit("/actions/refresh", { method: "GET", body });

/**
 * Busy times. The controller loads `doorBusyTimes` for the filters in the
 * query string, so a filter change is a navigation back to it.
 */
const BusyTimes = ({ api, status, filters }) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();
  const { timeZone } = status;
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const waiting = !status.lastEventAt;
  const data = waiting ? emptyBusy(filters, timeZone) : locals.doorBusyTimes;
  const failed = !data || data.error || data.forbidden;
  const setFilters = (next) => refresh(busyBody(next));

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api("door-busy-times-export", {
        query: busyQuery(filters, timeZone),
      });
      if (!res.ok) throw new Error(String(res.status));
      const filename = await downloadCsv(res, "busy-times.csv");
      setToast(t("doorAccess.busy.exported", { filename }));
    } catch {
      setToast(t("doorAccess.export.failed"));
    } finally {
      setExporting(false);
    }
  };

  let body;
  if (failed) {
    body = (
      <div className="door-card">
        <DoorEmpty
          icon="/img/new-icons/warning-white.svg"
          tone="danger"
          title={t("doorAccess.busy.errorTitle")}
          text={t("doorAccess.states.errorText")}
          actions={
            <button
              type="button"
              data-button-type="secondary"
              onClick={() => refresh({})}
            >
              <RetryLabel />
            </button>
          }
        />
      </div>
    );
  } else if (waiting) {
    body = (
      <div className="door-busy">
        <div className="door-busy-grid">
          <BusyHeatmap
            busy={data}
            waiting
            metrics={METRICS}
            caption={t(`doorAccess.busy.caption.${data.metric}`, {
              range: formatRange(data),
            })}
            onMetric={(metric) => setFilters({ ...filters, metric })}
          />
          <PeaksCard busy={data} waiting />
        </div>
      </div>
    );
  } else if (data.noData) {
    const fmt = (day) => moment.utc(day, "YYYY-MM-DD").format("MMM D, YYYY");
    body = (
      <div className="door-card">
        <DoorEmpty
          title={t("doorAccess.busy.noResultsTitle")}
          text={t("doorAccess.busy.noResultsText", {
            from: fmt(data.effectiveFrom),
            to: fmt(data.effectiveTo),
          })}
          actions={
            <button
              type="button"
              data-button-type="secondary"
              onClick={() => {
                setResetKey((key) => key + 1);
                setFilters(defaultBusyFilters());
              }}
            >
              {t("doorAccess.busy.backToDefault")}
            </button>
          }
        />
      </div>
    );
  } else {
    body = (
      <div className="door-busy">
        {data.lessThanSevenDays && (
          <div className="door-notice" data-tone="info">
            <SvgIcon
              src="/img/new-icons/info-circle.svg"
              useCurrentColor
              classNames="w-6 h-6 shrink-0"
            />
            <div className="door-grow">
              <span className="door-notice__overline">
                {t("doorAccess.busy.lowData.title", {
                  count: data.daysCounted,
                })}
              </span>
              <p>{t("doorAccess.busy.lowData.text")}</p>
            </div>
          </div>
        )}
        <div className="door-busy-grid">
          <BusyHeatmap
            busy={data}
            metrics={METRICS}
            caption={t(`doorAccess.busy.caption.${data.metric}`, {
              range: formatRange(data),
            })}
            onMetric={(metric) => setFilters({ ...filters, metric })}
          />
          <PeaksCard busy={data} />
        </div>
        <DailyVisitsChart busy={data} timeZone={timeZone} />
      </div>
    );
  }

  return (
    <>
      {status.connectionQuiet && <QuietBanner status={status} />}
      <BusyFilters
        key={resetKey}
        filters={filters}
        timeZone={timeZone}
        locations={data?.locations ?? []}
        canExport={Boolean(status.can?.export) && !waiting}
        exporting={exporting}
        onChange={setFilters}
        onExport={exportCsv}
      />
      {body}
      {toast && <output className="door-toast">{toast}</output>}
    </>
  );
};

export default BusyTimes;
